/**
 * VirtualFileSystem Service
 * 
 * Verwendet ZenFS mit OPFS Backend für persistente Speicherung im Browser.
 * Gilt NUR für Projekte mit version: "2.0"!
 */

import { configure, fs } from '@zenfs/core';
import { WebAccess } from '@zenfs/dom';
import type { VFSEntry, VFSChangeEvent, VFSChangeListener, FileDefinition } from './types';

class VirtualFileSystem {
  private initialized = false;
  private listeners: Set<VFSChangeListener> = new Set();
  private opfsRoot: FileSystemDirectoryHandle | null = null;

  /**
   * Initialisiert das VFS mit OPFS Backend
   */
  async init(): Promise<void> {
    if (this.initialized) return;

    try {
      // OPFS Root Handle holen
      this.opfsRoot = await navigator.storage.getDirectory();
      
      // ZenFS mit WebAccess (OPFS) Backend konfigurieren
      await configure({
        mounts: {
          '/': { backend: WebAccess, handle: this.opfsRoot }
        }
      });

      this.initialized = true;
      console.log('[VFS] Initialized with OPFS backend');
    } catch (error) {
      console.error('[VFS] Failed to initialize:', error);
      throw new Error('VFS initialization failed. OPFS may not be supported in this browser.');
    }
  }

  /**
   * Prüft ob VFS initialisiert ist
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Prüft ob OPFS im Browser unterstützt wird
   */
  static isSupported(): boolean {
    return 'storage' in navigator && 'getDirectory' in navigator.storage;
  }

  /**
   * Erstellt den Projekt-Root-Pfad
   */
  getProjectRoot(projectId: string): string {
    return `/projects/${projectId}`;
  }

  /**
   * Stellt sicher, dass ein Verzeichnis existiert
   */
  async ensureDir(path: string): Promise<void> {
    await this.init();
    try {
      await fs.promises.mkdir(path, { recursive: true });
    } catch (error: any) {
      // Ignoriere EEXIST Fehler
      if (error.code !== 'EEXIST') {
        throw error;
      }
    }
  }

  /**
   * Prüft ob eine Datei/Verzeichnis existiert
   */
  async exists(path: string): Promise<boolean> {
    await this.init();
    try {
      await fs.promises.stat(path);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Liest eine Datei
   */
  async readFile(path: string): Promise<string> {
    await this.init();
    const content = await fs.promises.readFile(path, 'utf-8');
    return content as string;
  }

  /**
   * Schreibt eine Datei
   */
  async writeFile(path: string, content: string): Promise<void> {
    await this.init();
    
    // Stelle sicher, dass das übergeordnete Verzeichnis existiert
    const parentDir = path.substring(0, path.lastIndexOf('/'));
    if (parentDir) {
      await this.ensureDir(parentDir);
    }
    
    await fs.promises.writeFile(path, content, 'utf-8');
    this.notifyListeners({ type: 'update', path });
  }

  /**
   * Löscht eine Datei oder ein Verzeichnis
   */
  async delete(path: string): Promise<void> {
    await this.init();
    const stat = await fs.promises.stat(path);
    
    if (stat.isDirectory()) {
      await fs.promises.rm(path, { recursive: true });
    } else {
      await fs.promises.unlink(path);
    }
    
    this.notifyListeners({ type: 'delete', path });
  }

  /**
   * Benennt eine Datei/Verzeichnis um
   */
  async rename(oldPath: string, newPath: string): Promise<void> {
    await this.init();
    await fs.promises.rename(oldPath, newPath);
    this.notifyListeners({ type: 'rename', path: oldPath, newPath });
  }

  /**
   * Listet Dateien in einem Verzeichnis
   */
  async readdir(path: string): Promise<VFSEntry[]> {
    await this.init();
    
    const entries = await fs.promises.readdir(path, { withFileTypes: true });
    const result: VFSEntry[] = [];
    
    for (const entry of entries) {
      const fullPath = `${path}/${entry.name}`;
      const stat = await fs.promises.stat(fullPath);
      
      result.push({
        name: entry.name,
        path: fullPath,
        type: entry.isDirectory() ? 'directory' : 'file',
        size: stat.size,
        modifiedAt: stat.mtime
      });
    }
    
    return result.sort((a, b) => {
      // Verzeichnisse zuerst, dann alphabetisch
      if (a.type !== b.type) {
        return a.type === 'directory' ? -1 : 1;
      }
      return a.name.localeCompare(b.name);
    });
  }

  /**
   * Lädt alle Dateien eines Projekts basierend auf der project.json Definition
   */
  async loadProjectFiles(projectId: string, files: FileDefinition[]): Promise<Map<string, string>> {
    await this.init();
    
    const projectRoot = this.getProjectRoot(projectId);
    const fileContents = new Map<string, string>();
    
    for (const fileDef of files) {
      const fullPath = `${projectRoot}/${fileDef.path}`;
      
      try {
        if (await this.exists(fullPath)) {
          const content = await this.readFile(fullPath);
          fileContents.set(fileDef.path, content);
        } else {
          // Datei existiert noch nicht - leeren Inhalt setzen
          fileContents.set(fileDef.path, '');
        }
      } catch (error) {
        console.warn(`[VFS] Failed to load file ${fullPath}:`, error);
        fileContents.set(fileDef.path, '');
      }
    }
    
    return fileContents;
  }

  /**
   * Speichert alle Dateien eines Projekts
   */
  async saveProjectFiles(projectId: string, files: Map<string, string>): Promise<void> {
    await this.init();
    
    const projectRoot = this.getProjectRoot(projectId);
    await this.ensureDir(projectRoot);
    
    for (const [relativePath, content] of files) {
      const fullPath = `${projectRoot}/${relativePath}`;
      await this.writeFile(fullPath, content);
    }
  }

  /**
   * Initialisiert ein neues Projekt mit den Dateien aus der project.json Definition
   */
  async initProject(projectId: string, files: FileDefinition[], initialContents?: Map<string, string>): Promise<void> {
    await this.init();
    
    const projectRoot = this.getProjectRoot(projectId);
    await this.ensureDir(projectRoot);
    
    for (const fileDef of files) {
      const fullPath = `${projectRoot}/${fileDef.path}`;
      
      // Prüfe ob Datei schon existiert
      if (!(await this.exists(fullPath))) {
        // Initialer Inhalt aus Map oder leerer String
        const content = initialContents?.get(fileDef.path) || '';
        await this.writeFile(fullPath, content);
      }
    }
    
    console.log(`[VFS] Project ${projectId} initialized with ${files.length} files`);
  }

  /**
   * Löscht ein komplettes Projekt
   */
  async deleteProject(projectId: string): Promise<void> {
    await this.init();
    
    const projectRoot = this.getProjectRoot(projectId);
    
    if (await this.exists(projectRoot)) {
      await this.delete(projectRoot);
      console.log(`[VFS] Project ${projectId} deleted`);
    }
  }

  /**
   * Registriert einen Change-Listener
   */
  onChange(listener: VFSChangeListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /**
   * Benachrichtigt alle Listener über eine Änderung
   */
  private notifyListeners(event: VFSChangeEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (error) {
        console.error('[VFS] Listener error:', error);
      }
    }
  }
}

// Singleton-Instanz
export const vfs = new VirtualFileSystem();

// Export für direkten Zugriff
export { VirtualFileSystem };

